import { FC } from 'react';

import { CommonInit } from '@app/types/user.type';
import { route_path } from '@app/utils/route_path';
import { useTranslation } from '@app/app/i18n';
import { LocalizedLink } from '@app/shared/ui/localized-link';

import {
  cvaFoot,
  cvaFootInner,
  cvaFootTitle,
  cvaFootBtn,
  cvaFootLink,
  cvaFootBlock2,
  cvaFootBlock3,
  cvaFootSocial,
  cvaFootLegal,
  cvaFootLegalLink,
  cvaFootLegalSeparator,
  cvaFootSupportLink,
} from './footer-styles';

type FooterProps = {
  commonContext?: CommonInit;
};

const Footer: FC<FooterProps> = ({ commonContext }) => {
  const { t } = useTranslation('footer');

  if (commonContext?.boxed) {
    return null;
  }

  return (
    <footer className={cvaFoot()}>
      <div className={cvaFootInner()}>
        <div>
          <div className={cvaFootTitle()} dangerouslySetInnerHTML={{ __html: t('title') }} />
          <LocalizedLink to={route_path.textToSlides} className={cvaFootBtn()}>
            {t('cta')}
          </LocalizedLink>
        </div>
        <div className={cvaFootBlock2()}>
          <LocalizedLink className={cvaFootLink()} to="/">
            {t('links.homepage')}
          </LocalizedLink>
          {!commonContext?.boxed && (
            <LocalizedLink className={cvaFootLink()} to="/features/">
              {t('links.features')}
            </LocalizedLink>
          )}
          {!commonContext?.boxed && (
            <LocalizedLink className={cvaFootLink()} to="/pricing/">
              {t('links.pricing')}
            </LocalizedLink>
          )}
          {!commonContext?.boxed && (
            <a className={cvaFootLink()} href={route_path.blog}>
              {t('links.blog')}
            </a>
          )}
          <LocalizedLink className={cvaFootLink()} to="/about/">
            {t('links.about')}
          </LocalizedLink>
        </div>
        <div className={cvaFootBlock3()}>
          <div className="flex items-center gap-2">
            {t('productOf')}
            {svgIcons.everypixel}
          </div>
          <div>
            {t('support.question')}
            <br />
            {t('support.reachOut')}{' '}
            <a className={cvaFootSupportLink()} href="mailto:support@wonderslide.com">
              support@wonderslide.com
            </a>
          </div>
          <div>
            {!commonContext?.boxed && (
              <span dangerouslySetInnerHTML={{ __html: t('social.follow') }} />
            )}
            <div className={cvaFootSocial()}>
              {!commonContext?.boxed && (
                <>
                  <a
                    href="https://www.linkedin.com/company/wonderslide"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <path
                        d="M24.2396 0.560059H3.75957C1.99317 0.560059 0.55957 1.99366 0.55957 3.76006V24.2401C0.55957 26.0065 1.99317 27.4401 3.75957 27.4401H24.2396C26.006 27.4401 27.4396 26.0065 27.4396 24.2401V3.76006C27.4396 1.99366 26.006 0.560059 24.2396 0.560059ZM8.87957 10.8001V22.9601H5.03957V10.8001H8.87957ZM5.03957 7.26086C5.03957 6.36486 5.80757 5.68006 6.95957 5.68006C8.11157 5.68006 8.83477 6.36486 8.87957 7.26086C8.87957 8.15686 8.16277 8.88006 6.95957 8.88006C5.80757 8.88006 5.03957 8.15686 5.03957 7.26086ZM22.9596 22.9601H19.1196C19.1196 22.9601 19.1196 17.0337 19.1196 16.5601C19.1196 15.2801 18.4796 14.0001 16.8796 13.9745H16.8284C15.2796 13.9745 14.6396 15.2929 14.6396 16.5601C14.6396 17.1425 14.6396 22.9601 14.6396 22.9601H10.7996V10.8001H14.6396V12.4385C14.6396 12.4385 15.8748 10.8001 18.358 10.8001C20.8988 10.8001 22.9596 12.5473 22.9596 16.0865V22.9601Z"
                        fill="#18AB86"
                      />
                    </svg>
                  </a>
                  <a href="https://twitter.com/wonderslide" target="_blank" rel="noreferrer">
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <path
                        d="M5.03957 0.560059C2.56533 0.560059 0.55957 2.56582 0.55957 5.04006V22.9601C0.55957 25.4343 2.56533 27.4401 5.03957 27.4401H22.9596C25.4338 27.4401 27.4396 25.4343 27.4396 22.9601V5.04006C27.4396 2.56582 25.4338 0.560059 22.9596 0.560059H5.03957ZM6.37457 6.32006H11.4546L15.0621 11.4463L19.4396 6.32006H21.0396L15.7846 12.4726L22.2646 21.6801H17.1858L12.9996 15.7326L7.91957 21.6801H6.31957L12.2771 14.7063L6.37457 6.32006ZM8.82457 7.60006L17.8533 20.4001H19.8146L10.7858 7.60006H8.82457Z"
                        fill="#18AB86"
                      />
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=100094292552746"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                      <path
                        d="M24.2396 0.560059H3.75957C1.99317 0.560059 0.55957 1.99366 0.55957 3.76006V24.2401C0.55957 26.0065 1.99317 27.4401 3.75957 27.4401H24.2396C26.006 27.4401 27.4396 26.0065 27.4396 24.2401V3.76006C27.4396 1.99366 26.006 0.560059 24.2396 0.560059ZM21.6796 10.1601H20.3996C19.03 10.1601 18.4796 10.4801 18.4796 11.4401V13.3601H21.6796L21.0396 16.5601H18.4796V26.1601H15.2796V16.5601H12.7196V13.3601H15.2796V11.4401C15.2796 8.88006 16.5596 6.96006 19.1196 6.96006C20.9756 6.96006 21.6796 7.60006 21.6796 7.60006V10.1601Z"
                        fill="#18AB86"
                      />
                    </svg>
                  </a>
                </>
              )}
            </div>
          </div>
          <div className={cvaFootLegal()}>
            {!commonContext?.boxed && (
              <LocalizedLink to="/legal/terms/" className={cvaFootLegalLink()}>
                {t('legal.termsOfUse')}
              </LocalizedLink>
            )}
            {!commonContext?.boxed && (
              <>
                <span className={cvaFootLegalSeparator()}>&#8226;</span>
                <LocalizedLink to="/legal/privacy/" className={cvaFootLegalLink()}>
                  {t('legal.privacyPolicy')}
                </LocalizedLink>
              </>
            )}
            {!commonContext?.boxed && (
              <>
                <span className={cvaFootLegalSeparator()}>&#8226;</span>
                <LocalizedLink to="/legal/cookie/" className={cvaFootLegalLink()}>
                  {t('legal.cookiePolicy')}
                </LocalizedLink>
              </>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export { Footer };

const svgIcons = {
  everypixel: (
    <svg
      width="117"
      height="12"
      viewBox="0 0 117 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {' '}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0 10.932C0 11.0833 0.121034 11.2194 0.287457 11.2194H6.44509C6.61151 11.2194 6.73254 11.0833 6.73254 10.932V9.69136C6.73254 9.54007 6.61151 9.40391 6.44509 9.40391H1.96681V6.75628H5.70375C5.85504 6.75628 5.99121 6.63524 5.99121 6.46882V5.21309C5.99121 5.06179 5.85504 4.92563 5.70375 4.92563H1.96681V2.44442H6.44509C6.61151 2.44442 6.73254 2.30826 6.73254 2.15697V0.916363C6.73254 0.76507 6.61151 0.628906 6.44509 0.628906H0.287457C0.121034 0.628906 0 0.76507 0 0.916363V10.932ZM15.7244 11.2043C15.7698 11.2951 15.8454 11.3707 15.9816 11.3707H16.1329C16.2691 11.3707 16.3447 11.2951 16.3901 11.2043L21.0802 1.02227C21.1709 0.825587 21.0499 0.628906 20.823 0.628906H19.2798C19.1436 0.628906 19.0528 0.719682 19.0226 0.795329L16.1178 7.30093H16.0119L13.107 0.795329C13.0768 0.719682 12.986 0.628906 12.8498 0.628906H11.3066C11.0797 0.628906 10.9587 0.825587 11.0494 1.02227L15.7244 11.2043ZM25.7803 10.932C25.7803 11.0833 25.9014 11.2194 26.0678 11.2194H32.2254C32.3919 11.2194 32.5129 11.0833 32.5129 10.932V9.69136C32.5129 9.54007 32.3919 9.40391 32.2254 9.40391H27.7472V6.75628H31.4841C31.6354 6.75628 31.7716 6.63524 31.7716 6.46882V5.21309C31.7716 5.06179 31.6354 4.92563 31.4841 4.92563H27.7472V2.44442H32.2254C32.3919 2.44442 32.5129 2.30826 32.5129 2.15697V0.916363C32.5129 0.76507 32.3919 0.628906 32.2254 0.628906H26.0678C25.9014 0.628906 25.7803 0.76507 25.7803 0.916363V10.932ZM38.2066 10.932V0.916363C38.2066 0.76507 38.3276 0.628906 38.494 0.628906H42.821C44.6365 0.628906 46.1192 2.08132 46.1192 3.88171C46.1192 5.2736 45.1963 6.4083 43.88 6.93783L45.9528 10.7807C46.0587 10.9774 45.9528 11.2194 45.6956 11.2194H44.107C43.9708 11.2194 43.8952 11.1438 43.8649 11.0833L41.8527 7.07399H40.1734V10.932C40.1734 11.0833 40.0372 11.2194 39.8859 11.2194H38.494C38.3276 11.2194 38.2066 11.0833 38.2066 10.932ZM40.1885 5.45516H42.6546C43.4564 5.45516 44.1524 4.75921 44.1524 3.91197C44.1524 3.11011 43.4564 2.44442 42.6546 2.44442H40.1885V5.45516ZM53.8402 10.932C53.8402 11.0833 53.9612 11.2194 54.1276 11.2194H55.5195C55.6708 11.2194 55.807 11.0833 55.807 10.932V6.10572L59.3019 1.06766C59.4229 0.870975 59.3019 0.628906 59.0598 0.628906H57.5015C57.3804 0.628906 57.3048 0.704553 57.2594 0.76507L54.8387 4.24481L52.418 0.76507C52.3726 0.704553 52.297 0.628906 52.1759 0.628906H50.6328C50.3907 0.628906 50.2697 0.870975 50.3907 1.06766L53.8402 6.12085V10.932ZM64.1634 10.932V0.916363C64.1634 0.76507 64.2844 0.628906 64.4509 0.628906H68.0063C69.8672 0.628906 71.3952 2.14184 71.3952 3.97248C71.3952 5.84852 69.8672 7.37658 68.0214 7.37658H66.1302V10.932C66.1302 11.0833 65.994 11.2194 65.8428 11.2194H64.4509C64.2844 11.2194 64.1634 11.0833 64.1634 10.932ZM66.1302 5.5308H67.8852C68.7325 5.5308 69.4435 4.84998 69.4435 3.95735C69.4435 3.12524 68.7325 2.48981 67.8852 2.48981H66.1302V5.5308ZM76.2366 10.932C76.2366 11.0833 76.3728 11.2194 76.5241 11.2194H77.9311C78.0824 11.2194 78.2185 11.0833 78.2185 10.932V0.916363C78.2185 0.76507 78.0824 0.628906 77.9311 0.628906H76.5241C76.3728 0.628906 76.2366 0.76507 76.2366 0.916363V10.932ZM98.043 10.932C98.043 11.0833 98.164 11.2194 98.3304 11.2194H104.488C104.654 11.2194 104.776 11.0833 104.776 10.932V9.69136C104.776 9.54007 104.654 9.40391 104.488 9.40391H100.01V6.75628H103.747C103.898 6.75628 104.034 6.63524 104.034 6.46882V5.21309C104.034 5.06179 103.898 4.92563 103.747 4.92563H100.01V2.44442H104.488C104.654 2.44442 104.776 2.30826 104.776 2.15697V0.916363C104.776 0.76507 104.654 0.628906 104.488 0.628906H98.3304C98.164 0.628906 98.043 0.76507 98.043 0.916363V10.932ZM110.469 10.932C110.469 11.0833 110.59 11.2194 110.757 11.2194H116.294C116.46 11.2194 116.581 11.0833 116.581 10.932V9.69136C116.581 9.54007 116.46 9.40391 116.294 9.40391H112.436V0.916363C112.436 0.76507 112.3 0.628906 112.149 0.628906H110.757C110.59 0.628906 110.469 0.76507 110.469 0.916363V10.932Z"
        fill="white"
      />{' '}
      <path d="M84.8603 1.13322H83.3474V2.64615H84.8603V1.13322Z" fill="white" />{' '}
      <path d="M86.8775 3.15046H85.3646V4.66339H86.8775V3.15046Z" fill="white" />{' '}
      <path d="M86.8775 7.18494H85.3646V8.69787H86.8775V7.18494Z" fill="white" />{' '}
      <path d="M84.8603 9.20218H83.3474V10.7151H84.8603V9.20218Z" fill="white" />{' '}
      <path d="M92.9293 9.20218H91.4163V10.7151H92.9293V9.20218Z" fill="white" />{' '}
      <path d="M90.912 7.18494H89.3991V8.69787H90.912V7.18494Z" fill="white" />{' '}
      <path d="M88.8948 5.1677H87.3819V6.68063H88.8948V5.1677Z" fill="white" />{' '}
      <path d="M90.912 3.15046H89.3991V4.66339H90.912V3.15046Z" fill="white" />{' '}
      <path d="M92.9293 1.13322H91.4163V2.64615H92.9293V1.13322Z" fill="white" />{' '}
    </svg>
  ),
};
